import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar } from "@/components/Brand";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ProfileSetup() {
  const { user, signIn } = useAuth();
  const [f, setF] = useState({ name: user.name || "", display_name: user.display_name || user.name || "" });
  const [pic, setPic] = useState(user.picture);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const open = user.role === "student" && !user.profile_complete;

  const refresh = (data) => signIn(localStorage.getItem(TOKEN_KEY), data);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPic(data.picture); refresh(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };

  const finish = async () => {
    if (!f.name.trim()) return toast.error("Please add your name");
    setBusy(true);
    try {
      const { data } = await api.patch("/profile", { name: f.name, display_name: f.display_name || f.name, profile_complete: true });
      refresh(data);
      toast.success("Welcome! Your profile is all set.");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-md" data-testid="profile-setup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Welcome to AVY Builder</DialogTitle>
          <DialogDescription>Set up your personal workspace profile. This is yours only — other associates can't see it.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center py-2">
          <div className="relative">
            <Avatar user={{ name: f.display_name || f.name, picture: pic }} size={96} testId="setup-avatar" />
            <button type="button" onClick={() => fileRef.current?.click()} className="absolute bottom-0 right-0 grid h-8 w-8 place-items-center rounded-full bg-[#3B185F] text-white" data-testid="setup-photo-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="setup-photo-input" />
          </div>
          <p className="mt-2 text-xs text-stone-400">Upload a profile photo</p>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="setup-name-input" /></div>
          <div className="space-y-1.5"><Label>Display name</Label><Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} placeholder="How you'd like to be shown" data-testid="setup-displayname-input" /></div>
          <Button onClick={finish} disabled={busy} className="w-full bg-[#3B185F] hover:bg-[#250E3F]" data-testid="setup-finish-btn">{busy ? "Saving…" : "Enter my workspace"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
